import type { InstallSpec } from "../install/types.ts";
import type { EmptyAccesses } from "../types/lifecycle.ts";
import type { Maturity, ResourceConfigUse } from "./shared.ts";

type BaseRegisteredIntegration = {
  /**Document paths, relative to the document root for this integration's category */
  docs: Partial<Record<"install" | "request", string>>;
  /**If present, installation / visibility is gated on this feature flag */
  flag?: string | undefined;
  /**This integration's human-readable name */
  label: string;
  /**This integration's release level */
  maturity: Maturity;
  /**Publicly accessible URL of this integration's logo
   *
   * Should be external to P0 to avoid Copyright issues.
   */
  logoUrl: string;
};

/** Defines which filters are available for this resource
 *
 * A filter has:
 * - `id`: indicates which access parameter is filtered
 * - `key`: indicates what value is matched in order to filter
 *
 * For example, ssh sessions could be filtered with ID equal to
 * "destination" and key equal to "public-ip", in which case
 * available destinations will be filtered to matching public IP
 * addresses.
 *
 * Keys of this schema correspond to filter IDs.
 */
export type AnyFilterSchema = Readonly<
  Record<
    string,
    {
      /** Possible keys for this filter */
      keys: Readonly<string[]> | "boolean" | "string";
      /** A human-readable label for this filter id */
      label?: string;
      /** If present, maps key values to human-readable labels */
      labeler?: Record<string, string>;
    }
  >
>;

/** An integration that supports an access lifecycle */
type ResourceIntegration<R extends AnyFilterSchema> = {
  /**This integration's access */
  accesses: EmptyAccesses;
  /**Whether this is an IdP or an accessible resource */
  category: "directory" | "resource";
  /**Integration filter rule schema */
  filters: R;
  /**Whether access can be requested directly */
  requestType: "bridge" | "requestable";
  /**Which components are available for which uses */
  useMapping: { use: ResourceConfigUse; component: string }[];
};

/** An integration that does not support an access lifecycle */
type NonResourceIntegration = { category: "other" };

/** An integration that installs using the generic installer */
type ComponentIntegration<C extends InstallSpec> = {
  /**Install components for the integration, or "oauth" if this is an
   * OAuth integration.
   *
   * May be `undefined` if this has no installer.
   */
  components: C;
  /**Returns the label for a component's identifier
   *
   * E.g. "identifier" if the component is installed per AWS account.
   *
   * Defaults to "identifier" if absent.
   */
  identifier?: string | ((component: keyof C) => string);
  /**Returns the descriptor for a component
   *
   * E.g. "account" if the component is installed per AWS account
   */
  itemKind: string | ((component: keyof C) => string);
};

/** An integration that is installed via OAuth */
type OAuthIntegration = {
  /**"oauth" if this is an OAuth integration.
   */
  components: "oauth";
};

/** An integration that can not be installed */
type UninstallableIntegration = {
  /**"undefined" if this can not be installed */
  components: undefined;
};

/** An integration that installs using the generic installer */
export type RegisteredComponentIntegration<C extends InstallSpec> =
  BaseRegisteredIntegration & ComponentIntegration<C>;

/** Any integration */
export type RegisteredResourceIntegration = BaseRegisteredIntegration &
  ResourceIntegration<AnyFilterSchema> &
  (
    | ComponentIntegration<InstallSpec>
    | OAuthIntegration
    | UninstallableIntegration
  );

/** Create a new integration registry entry */
export const Registration = <
  C extends InstallSpec,
  R extends BaseRegisteredIntegration &
    (NonResourceIntegration | ResourceIntegration<AnyFilterSchema>) &
    (ComponentIntegration<C> | OAuthIntegration | UninstallableIntegration),
>(
  registration: R
): R => registration;
